import { clsx } from "clsx";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { CreditGroup } from "@/types/credit-groups";

import Icon from "../Icon";

type Props = {
  item: CreditGroup;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

export default function NADNRPlanCard({ item }: Props) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const plan = item.associated_payment_plan;
  const dosage = item.plan_dosage_information;
  const bookableServices = dosage?.bookable_services ?? [];
  const paymentsRemaining = plan?.payment_plan_billing_cycles_required ?? 0;

  const handleBook = () =>
    navigate(`/book?store=${encodeURIComponent(item.store_code)}`);

  return (
    <div className="relative mb-3 flex w-full flex-col rounded-[10px] border border-neutral-lightest bg-clear-sky shadow-[0_4px_4px_0_rgba(174,174,174,0.25)] last:mb-0">
      {/* Header */}
      <div className="flex justify-between gap-7 p-4">
        <div className="flex flex-col items-start gap-2">
          <span className="font-poppins text-sm font-semibold leading-[14px] text-deep-blue">
            {item.group_service_name}
          </span>
          <span className="font-poppins text-xs text-[#9ca3af]">
            {item.store_description}
          </span>
        </div>
        <button type="button" onClick={handleBook} className="cursor-pointer">
          <Icon name="calendar-plus" />
        </button>
      </div>

      {/* Plan Summary */}
      {plan && (
        <div className="flex flex-col border-t border-[#d1d5db] p-4 font-poppins text-xs leading-[133.33%] text-neutral-darkest">
          <span>Loading Dose Plan:</span>
          <ul className="list-inside list-disc pl-2">
            {paymentsRemaining > 0 && (
              <li>
                Payments Remaining: <strong>{paymentsRemaining}</strong>
              </li>
            )}
            {item.next_expiration_datetime && (
              <li>
                Doses Expire:{" "}
                <strong>{formatDate(item.next_expiration_datetime)}</strong>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Plan Details Toggle */}
      {dosage && (
        <div className="border-t border-[#d1d5db] bg-[#e6f0f9]">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex w-full items-center justify-between gap-2 p-4"
          >
            <strong className="font-poppins text-sm font-semibold leading-[14px] text-deep-blue">
              Plan Details
            </strong>
            <Icon
              name="arrow"
              className={clsx(
                "mr-3.5 size-4 -rotate-90 text-neutral-darkest transition-all",
                open && "rotate-90",
              )}
            />
          </button>

          {/* Plan Details Content */}
          {open && (
            <div
              className={clsx(
                "max-h-0 w-full overflow-hidden p-4 font-poppins text-sm leading-[140%] text-neutral-dark transition-all",
                open && "max-h-96",
              )}
            >
              {bookableServices.length > 0 ? (
                <>
                  <p>
                    You have <strong>{dosage?.remaining_dosage} of NAD+</strong>{" "}
                    available, which can be used in the following ways:
                  </p>
                  <div>
                    {bookableServices.map((service) => (
                      <strong key={service}>
                        {" • "}
                        {service}
                      </strong>
                    ))}
                  </div>
                  <p>
                    Choose the option that best supports your wellness journey.
                  </p>
                </>
              ) : (
                <p>
                  You have less than 125mg of <strong>NAD+</strong> available.
                  At this time, no services can be booked with your remaining
                  balance.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
